/**
 * SupportDelivery — White-Label → Support: "Send a test report" and how the
 * last report fared.
 *
 * The test report is a clearly marked item on the board this organization's
 * reports go to; it is offered once there is a board and a connection to
 * deliver through, and the page follows it until it is delivered or failing.
 * "Last delivery" is real: delivered (when, with a link to the item), sending,
 * failing since … with the reason in plain words and the next try, or given
 * up after a day.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Button, Tag } from 'antd';
import { CheckCircleFilled, ClockCircleOutlined, ExclamationCircleFilled, ExportOutlined, SendOutlined } from '@ant-design/icons';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { activeLocale } from '@/utils/locale';
import { safeHttpUrl } from '@/utils/safeHttpUrl';
import { type Draft, type SupportView, deliveringConnection, deliveryLine } from './supportBoardModel';

const P = 'whiteLabel.supportBoard.delivery';
const FOLLOW_EVERY_MS = 2500;
const FOLLOW_FOR_MS = 60_000;

interface Props {
  tenantId: string;
  view: SupportView;
  draft: Draft;
  dirty: boolean;
  onView: (view: SupportView) => void;
}

function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(activeLocale(), { dateStyle: 'medium', timeStyle: 'short' });
}

export default function SupportDelivery({ tenantId, view, draft, dirty, onView }: Props) {
  const { t } = useLanguage();
  const uid = useId();
  const base = `/super-admin/tenants/${tenantId}/support-board`;
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const follow = useRef<number | null>(null);

  useEffect(() => () => { if (follow.current) window.clearInterval(follow.current); }, []);

  const hasBoard = !!view.effective;
  const connected = !!deliveringConnection(view, draft);
  const why = dirty ? 'saveFirst' : !hasBoard ? 'noBoard' : !connected ? 'notConnected' : null;
  const canTest = !why && view.delivery.test_available;

  const sendTest = async () => {
    setSending(true);
    setError(null);
    try {
      const res = await api.post(`${base}/test-report`);
      onView(res.data);
      const started = Date.now();
      if (follow.current) window.clearInterval(follow.current);
      follow.current = window.setInterval(async () => {
        try {
          const next = await api.get(base);
          onView(next.data);
          const last = next.data?.delivery?.last;
          const settled = !last || last.status === 'delivered' || last.status === 'failed' || last.error_code;
          if (settled || Date.now() - started > FOLLOW_FOR_MS) {
            if (follow.current) window.clearInterval(follow.current);
            follow.current = null;
          }
        } catch { /* the next tick asks again */ }
      }, FOLLOW_EVERY_MS);
    } catch (err) {
      setError((err as { response?: { data?: { error_code?: string } } })?.response?.data?.error_code || 'generic');
    } finally {
      setSending(false);
    }
  };

  const line = deliveryLine(view.delivery.last);
  const boardUrl = view.effective?.board_url ? safeHttpUrl(view.effective.board_url) : null;
  const itemUrl = line.kind === 'delivered' && boardUrl && view.delivery.last?.monday_item_id
    ? `${boardUrl}/pulses/${view.delivery.last.monday_item_id}` : null;

  return (
    <div className="wl-sb-block" data-testid="wl-support-board-delivery">
      <span className="wl-sb-block-label">{t(`${P}.title`)}</span>
      <div className="wl-sb-delivery">
        <div className="wl-sb-delivery-row">
          <Button icon={<SendOutlined />} disabled={!canTest} loading={sending} onClick={() => { void sendTest(); }}
                  aria-describedby={why ? `${uid}-why` : undefined} data-testid="wl-support-board-test">
            {t(`${P}.testButton`)}
          </Button>
          {why ? (
            <span className="wl-sb-waiting" id={`${uid}-why`} data-testid="wl-support-board-test-why">
              {t(`${P}.why.${why}`)}
            </span>
          ) : (
            <span className="wl-sb-waiting">{t(`${P}.testHint`)}</span>
          )}
        </div>
        <dl className="wl-sb-facts">
          <dt>{t(`${P}.last`)}</dt>
          <dd data-testid="wl-support-board-last-delivery" className={`wl-sb-last wl-sb-last--${line.kind}`}>
            {line.kind === 'none' && t(`${P}.none`)}
            {line.kind === 'sending' && <><ClockCircleOutlined className="wl-sb-last-icon" aria-hidden /> {t(`${P}.sending`)}</>}
            {line.kind === 'delivered' && (<>
              <CheckCircleFilled className="wl-sb-last-icon" aria-hidden /> {t(`${P}.delivered`, { when: when(line.at) })}
              {line.isTest && <Tag className="wl-sb-test-tag">{t(`${P}.testTag`)}</Tag>}
              {itemUrl && (
                <a className="wl-sb-chip-open" href={itemUrl} target="_blank" rel="noopener noreferrer">
                  {t(`${P}.openItem`)} <ExportOutlined aria-hidden />
                </a>
              )}
            </>)}
            {line.kind === 'retrying' && (<>
              <ExclamationCircleFilled className="wl-sb-last-icon" aria-hidden />{' '}
              {t(`${P}.retrying`, { since: when(line.since), next: when(line.next),
                                    reason: t(`${P}.reasons.${line.code}`, { defaultValue: t(`${P}.reasons.internal`) }) })}
            </>)}
            {line.kind === 'failed' && (<>
              <ExclamationCircleFilled className="wl-sb-last-icon" aria-hidden />{' '}
              {t(`${P}.failed`, { reason: t(`${P}.reasons.${line.code}`, { defaultValue: t(`${P}.reasons.internal`) }) })}
            </>)}
          </dd>
        </dl>
        {error && (
          <div className="wl-sb-link-state wl-sb-link-state--error" role="alert">
            <ExclamationCircleFilled className="wl-sb-last-icon" aria-hidden />
            <span>{t(`whiteLabel.supportBoard.monday.errors.${error}`,
              { defaultValue: t('whiteLabel.supportBoard.monday.errors.generic') })}</span>
          </div>
        )}
      </div>
    </div>
  );
}
